import { Card, CardContent } from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import type {
  PortalVendorInvoiceDto,
  PortalVendorInvoiceStatus,
} from "../api/vendor";

// External party portals — Vendor finance alignment (PR-D, light-touch). An
// at-a-glance financial summary + a per-customer aggregate computed from the
// EXISTING /vendor/invoices read — no new endpoint, and the invoice list /
// detail / Stripe pay flow are untouched. "Outstanding" EXCLUDES processing
// (mirrors the staff VfiDashboard "in transit" split); void invoices are
// excluded from every total.

const OUTSTANDING_STATUSES: readonly PortalVendorInvoiceStatus[] = [
  "issued",
  "viewed",
  "overdue",
];

function money(amount: number): string {
  return formatCurrency(amount, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

interface Totals {
  invoiced: number;
  collected: number;
  outstanding: number;
  processing: number;
}

function accumulate(into: Totals, inv: PortalVendorInvoiceDto): void {
  into.invoiced += inv.amount;
  if (inv.status === "paid") into.collected += inv.amount;
  else if (inv.status === "processing") into.processing += inv.amount;
  else if (OUTSTANDING_STATUSES.includes(inv.status)) into.outstanding += inv.amount;
}

function emptyTotals(): Totals {
  return { invoiced: 0, collected: 0, outstanding: 0, processing: 0 };
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Card>
      <CardContent className="space-y-space-1">
        <p className="text-caption uppercase tracking-wide text-gray-mid">{label}</p>
        <div className="text-h4 font-semibold text-onyx">{value}</div>
        {sub && <p className="text-caption text-gray-mid">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export function VendorInvoiceSummary({ invoices }: { invoices: PortalVendorInvoiceDto[] }) {
  const billable = invoices.filter((i) => i.status !== "void");

  const totals = emptyTotals();
  for (const inv of billable) accumulate(totals, inv);
  const overdueCount = billable.filter((i) => i.status === "overdue").length;

  // Per-customer aggregate (across the vendor's customer loans).
  const byCustomer = new Map<string, Totals>();
  for (const inv of billable) {
    const cur = byCustomer.get(inv.loanDisplayName) ?? emptyTotals();
    accumulate(cur, inv);
    byCustomer.set(inv.loanDisplayName, cur);
  }
  const customers = [...byCustomer.entries()]
    .map(([name, t]) => ({ name, ...t }))
    .sort((a, b) => b.invoiced - a.invoiced);

  return (
    <div className="flex flex-col gap-space-4" data-testid="vendor-invoice-summary">
      <div className="grid gap-space-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Total invoiced" value={money(totals.invoiced)} />
        <Stat label="Collected" value={money(totals.collected)} />
        <Stat
          label="Outstanding"
          value={money(totals.outstanding)}
          sub={overdueCount > 0 ? `${overdueCount} overdue` : undefined}
        />
        <Stat label="In transit" value={money(totals.processing)} sub="Bank transfers clearing" />
      </div>

      {customers.length > 1 && (
        <div className="space-y-space-2">
          <h3 className="text-small font-semibold uppercase tracking-wide text-gray-mid">
            By customer ({customers.length})
          </h3>
          <div className="overflow-x-auto rounded-lg border border-border-light">
            <table className="w-full text-small">
              <thead>
                <tr className="border-b border-border-light bg-white-warm text-left text-caption uppercase tracking-wide text-gray-mid">
                  <th className="px-space-3 py-space-2 font-medium">Customer</th>
                  <th className="px-space-3 py-space-2 text-right font-medium">Invoiced</th>
                  <th className="px-space-3 py-space-2 text-right font-medium">Collected</th>
                  <th className="px-space-3 py-space-2 text-right font-medium">Outstanding</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((c) => (
                  <tr key={c.name} className="border-b border-border-light last:border-0">
                    <td className="px-space-3 py-space-2 text-onyx">{c.name}</td>
                    <td className="px-space-3 py-space-2 text-right text-gray-mid">
                      {money(c.invoiced)}
                    </td>
                    <td className="px-space-3 py-space-2 text-right text-gray-mid">
                      {money(c.collected)}
                    </td>
                    <td className="px-space-3 py-space-2 text-right text-onyx">
                      {money(c.outstanding)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
